import { useCallback, useState } from 'react';

/**
 * Graph node CRUD + demo helpers + delete dialog state.
 */
export const useGraphNodeOperations = ({
  graphStateRef,
  applyGraphAction,
  showFeedback,
  requestAutoFit,
  graphActions,
  getDescendantIds,
  createEmptyGraphState,
  buildGraphStateFromReactFlow,
  demoNodes,
  demoEdges,
  generateDemoTree,
  onCloseEdit,
  editNodeId,
  selectedNodeId,
  setSelectedNodeId,
  setExpandedNodeIds,
}) => {
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    nodeId: null,
    nodeLabel: '',
    descendantIds: [],
  });

  const getNextNodeId = useCallback(() => {
    const nodeIds = Object.keys(graphStateRef.current.nodesById || {});
    const numericIds = nodeIds
      .map((nodeId) => Number.parseInt(nodeId, 10))
      .filter((value) => Number.isFinite(value));

    const nextNumericId = numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1;
    return String(nextNumericId);
  }, [graphStateRef]);

  const createRootNode = useCallback(() => {
    const currentNodeCount = Object.keys(graphStateRef.current.nodesById || {}).length;
    if (currentNodeCount > 0) {
      showFeedback('Graph already contains nodes. Clear the graph before creating a new root.', 'warning');
      return false;
    }

    const rootId = getNextNodeId();
    const changed = applyGraphAction(
      {
        type: graphActions.ADD_NODE,
        payload: {
          id: rootId,
          parentId: null,
          node: {
            id: rootId,
            name: 'Root Cohort',
            status: 'unedited',
          },
        },
      },
      { recordHistory: true },
    );

    if (changed) {
      setSelectedNodeId(rootId);
      showFeedback('Root node created.', 'success');
      requestAutoFit({ padding: 0.34, duration: 520, maxZoom: 1 });
    }

    return changed;
  }, [applyGraphAction, getNextNodeId, graphActions.ADD_NODE, graphStateRef, requestAutoFit, setSelectedNodeId, showFeedback]);

  const addChild = useCallback(
    (parentId) => {
      const childId = getNextNodeId();
      const changed = applyGraphAction(
        {
          type: graphActions.ADD_NODE,
          payload: {
            id: childId,
            parentId,
            node: {
              id: childId,
              name: 'New Cohort',
              status: 'unedited',
            },
          },
        },
        { recordHistory: true },
      );

      if (changed) {
        setSelectedNodeId(childId);
        showFeedback('Child node added.', 'success');
        requestAutoFit({
          nodes: [{ id: parentId }, { id: childId }],
          padding: 0.36,
          duration: 520,
          maxZoom: 1,
        });
      }

      return changed;
    },
    [applyGraphAction, getNextNodeId, graphActions.ADD_NODE, requestAutoFit, setSelectedNodeId, showFeedback],
  );

  const insertBetween = useCallback(
    (childId) => {
      const current = graphStateRef.current;
      const child = current.nodesById?.[childId];
      if (!child || !child.parentId) {
        showFeedback('Insert is only available for non-root nodes.', 'warning');
        return false;
      }

      const insertedId = getNextNodeId();
      const changed = applyGraphAction(
        {
          type: graphActions.ADD_NODE,
          payload: {
            id: insertedId,
            parentId: child.parentId,
            insertBeforeChildId: childId,
            node: {
              id: insertedId,
              name: 'Inserted Cohort',
              status: 'unedited',
            },
          },
        },
        { recordHistory: true },
      );

      if (changed) {
        setSelectedNodeId(insertedId);
        showFeedback('Node inserted between parent and child.', 'success');
        requestAutoFit({ padding: 0.34, duration: 520, maxZoom: 1 });
      }

      return changed;
    },
    [applyGraphAction, getNextNodeId, graphActions.ADD_NODE, graphStateRef, requestAutoFit, setSelectedNodeId, showFeedback],
  );

  const toggleBreakpoint = useCallback(
    (nodeId) => {
      const current = graphStateRef.current;
      if (!current.nodesById?.[nodeId]) return false;

      const nextBreakpointNodeId = current.metadata?.breakpointNodeId === nodeId ? null : nodeId;
      const changed = applyGraphAction(
        {
          type: graphActions.SET_GRAPH,
          payload: {
            ...current,
            metadata: {
              ...current.metadata,
              breakpointNodeId: nextBreakpointNodeId,
            },
          },
        },
        { recordHistory: true },
      );

      if (changed) {
        showFeedback(nextBreakpointNodeId ? `Breakpoint set on node ${nodeId}.` : 'Breakpoint removed.', 'info');
      }

      return changed;
    },
    [applyGraphAction, graphActions.SET_GRAPH, graphStateRef, showFeedback],
  );

  const requestDeleteNode = useCallback(
    (nodeId) => {
      const current = graphStateRef.current;
      const node = current.nodesById?.[nodeId];
      if (!node) return;

      setDeleteDialog({
        open: true,
        nodeId,
        nodeLabel: node.name || 'this node',
        descendantIds: getDescendantIds(current, nodeId),
      });
    },
    [getDescendantIds, graphStateRef],
  );

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog({ open: false, nodeId: null, nodeLabel: '', descendantIds: [] });
  }, []);

  const confirmDeleteNode = useCallback(
    (cascadeDelete) => {
      const { nodeId, descendantIds } = deleteDialog;
      if (!nodeId) return false;

      const totalNodesBeforeDelete = Object.keys(graphStateRef.current.nodesById || {}).length;

      if (!cascadeDelete && descendantIds.length > 0) {
        showFeedback(
          'Delete only this node is not allowed because it would orphan child nodes. Use "Delete Node + Descendants".',
          'warning',
        );
        return false;
      }

      const changed = applyGraphAction(
        {
          type: graphActions.DELETE_NODE,
          payload: {
            nodeId,
            cascade: cascadeDelete,
          },
        },
        { recordHistory: true },
      );

      if (!changed) return false;

      if (editNodeId && (editNodeId === nodeId || descendantIds.includes(editNodeId))) {
        onCloseEdit?.();
      }

      if (selectedNodeId && (selectedNodeId === nodeId || descendantIds.includes(selectedNodeId))) {
        setSelectedNodeId(null);
      }

      const removedCount = cascadeDelete ? 1 + descendantIds.length : 1;
      const remainingNodeCount = Math.max(0, totalNodesBeforeDelete - removedCount);
      if (remainingNodeCount > 0) {
        requestAutoFit({ padding: 0.32, duration: 520, maxZoom: 1.02 });
      }

      closeDeleteDialog();
      showFeedback(
        cascadeDelete
          ? `Deleted node and ${descendantIds.length} descendant node${descendantIds.length === 1 ? '' : 's'}.`
          : 'Node deleted successfully.',
        'success',
      );

      return true;
    },
    [
      applyGraphAction,
      closeDeleteDialog,
      deleteDialog,
      editNodeId,
      graphActions.DELETE_NODE,
      graphStateRef,
      onCloseEdit,
      requestAutoFit,
      selectedNodeId,
      setSelectedNodeId,
      showFeedback,
    ],
  );

  const duplicateNode = useCallback(
    (sourceNodeId) => {
      const current = graphStateRef.current;
      const source = current.nodesById?.[sourceNodeId];
      if (!source) return false;

      if (source.parentId === null) {
        showFeedback('Duplicating the root node is not supported in a single-root graph.', 'warning');
        return false;
      }

      const snapshot = JSON.parse(JSON.stringify(current));
      const usedIds = new Set(Object.keys(snapshot.nodesById || {}));
      const numericIds = [...usedIds]
        .map((id) => Number.parseInt(id, 10))
        .filter((value) => Number.isFinite(value));
      let nextNumericId = numericIds.length > 0 ? Math.max(...numericIds) : 0;

      const nextId = () => {
        do {
          nextNumericId += 1;
        } while (usedIds.has(String(nextNumericId)));
        const id = String(nextNumericId);
        usedIds.add(id);
        return id;
      };

      const today = new Date().toISOString().slice(0, 10);

      const cloneSubtree = (oldNodeId, newParentId, isRootCopy) => {
        const oldNode = snapshot.nodesById[oldNodeId];
        const newNodeId = nextId();

        snapshot.nodesById[newNodeId] = {
          ...oldNode,
          id: newNodeId,
          name: isRootCopy ? `${oldNode.name} Copy` : oldNode.name,
          parentId: newParentId,
          childIds: [],
          patientCount: null,
          isExecuted: false,
          status: 'unedited',
          createdAt: today,
          lastModified: today,
        };

        (oldNode.childIds || []).forEach((oldChildId) => {
          const newChildId = cloneSubtree(oldChildId, newNodeId, false);
          snapshot.nodesById[newNodeId].childIds.push(newChildId);
        });

        return newNodeId;
      };

      const duplicatedRootId = cloneSubtree(sourceNodeId, source.parentId, true);

      const parent = snapshot.nodesById[source.parentId];
      const sourceIndex = parent.childIds.indexOf(sourceNodeId);
      if (sourceIndex >= 0) {
        parent.childIds.splice(sourceIndex + 1, 0, duplicatedRootId);
      } else {
        parent.childIds.push(duplicatedRootId);
      }

      const changed = applyGraphAction(
        {
          type: graphActions.SET_GRAPH,
          payload: snapshot,
        },
        { recordHistory: true },
      );

      if (changed) {
        setExpandedNodeIds((prev) => [...new Set([...prev, source.parentId, duplicatedRootId])]);
        setSelectedNodeId(duplicatedRootId);
        showFeedback('Cohort subtree duplicated.', 'success');
        requestAutoFit({ nodes: [{ id: duplicatedRootId }], padding: 0.4, duration: 500, maxZoom: 1.05 });
      }

      return changed;
    },
    [
      applyGraphAction,
      graphActions.SET_GRAPH,
      graphStateRef,
      requestAutoFit,
      setExpandedNodeIds,
      setSelectedNodeId,
      showFeedback,
    ],
  );

  const loadDemo = useCallback(() => {
    try {
      const demoGraph = buildGraphStateFromReactFlow(demoNodes, demoEdges, 'Cohort Graph Demo');
      const changed = applyGraphAction(
        {
          type: graphActions.SET_GRAPH,
          payload: demoGraph,
        },
        { recordHistory: true },
      );

      if (changed) {
        setSelectedNodeId(demoGraph.rootNodeId || null);
        showFeedback('Demo graph loaded.', 'success');
        requestAutoFit({ padding: 0.28, duration: 560, maxZoom: 1 });
      }

      return changed;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showFeedback(`Failed to load demo graph: ${message}`, 'error');
      return false;
    }
  }, [applyGraphAction, buildGraphStateFromReactFlow, demoEdges, demoNodes, graphActions.SET_GRAPH, requestAutoFit, setSelectedNodeId, showFeedback]);

  const generateLargeDemo = useCallback(() => {
    try {
      const largeDemo = generateDemoTree(60);
      const demoGraph = buildGraphStateFromReactFlow(
        largeDemo.nodes,
        largeDemo.edges,
        'Large Cohort Graph Demo',
      );

      const changed = applyGraphAction(
        {
          type: graphActions.SET_GRAPH,
          payload: demoGraph,
        },
        { recordHistory: true },
      );

      if (changed) {
        setSelectedNodeId(demoGraph.rootNodeId || null);
        showFeedback('60-node demo graph generated.', 'success');
        requestAutoFit({ padding: 0.2, duration: 650, maxZoom: 0.9 });
      }

      return changed;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showFeedback(`Failed to generate large demo graph: ${message}`, 'error');
      return false;
    }
  }, [applyGraphAction, buildGraphStateFromReactFlow, generateDemoTree, graphActions.SET_GRAPH, requestAutoFit, setSelectedNodeId, showFeedback]);

  const clearGraph = useCallback(() => {
    const changed = applyGraphAction(
      {
        type: graphActions.SET_GRAPH,
        payload: createEmptyGraphState('Cohort Graph Draft'),
      },
      { recordHistory: true },
    );

    if (changed) {
      setSelectedNodeId(null);
      showFeedback('Graph cleared.', 'info');
    }

    return changed;
  }, [applyGraphAction, createEmptyGraphState, graphActions.SET_GRAPH, setSelectedNodeId, showFeedback]);

  return {
    deleteDialog,
    requestDeleteNode,
    closeDeleteDialog,
    confirmDeleteNode,
    createRootNode,
    addChild,
    insertBetween,
    toggleBreakpoint,
    duplicateNode,
    loadDemo,
    generateLargeDemo,
    clearGraph,
  };
};

export default useGraphNodeOperations;
